import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import { 
  Typography, Box, Button, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, Snackbar, Backdrop, CircularProgress,
  styled, Stack, useTheme, Fade
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useNavigate } from "react-router-dom";
import { useThemeContext } from '../contexts/ThemeContext.tsx';

// --- Animációk ---
const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
`;

// --- Dinamikus Stílusok ---
const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode, theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
    backgroundSize: '200% 200%',
    animation: 'backgroundMove 12s ease infinite',
    color: darkMode ? 'white' : '#1a237e',
    padding: theme.spacing(1),
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
  },
  '& .MuiDialogTitle-root': { 
    fontWeight: 'bold', 
    textAlign: 'center', 
    fontSize: '1.4rem',
    color: darkMode ? 'white' : '#1a237e' 
  },
}));

const StyledTextField = styled(TextField, {
    shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiOutlinedInput-root': {
    color: darkMode ? 'white' : '#1a237e',
    borderRadius: '12px',
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)',
    '& fieldset': { borderColor: darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(26, 35, 126, 0.3)' },
    '&:hover fieldset': { borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(26, 35, 126, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: darkMode ? 'white' : '#1a237e' },
  },
  '& .MuiInputLabel-root': { color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(26, 35, 126, 0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: darkMode ? 'white' : '#1a237e' },
}));

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '8px 20px',
  fontWeight: 'bold',
  color: darkMode ? 'white' : '#ffffff',
  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.15)' : '#25379c',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.25)' : '#1a237e',
    transform: 'translateY(-2px)',
    boxShadow: darkMode ? '0 0 15px rgba(255, 64, 129, 0.4)' : '0 0 15px rgba(33, 203, 243, 0.4)',
  },
}));

const MyScore: React.FC = () => {
  const { darkMode } = useThemeContext();
  const isAuthenticated = !!Cookies.get('token');
  const [score, setScore] = useState<number>(0);
  const [token, setToken] = useState<number>(0);
  const [openTransfer, setOpenTransfer] = useState(false);
  const [openConvert, setOpenConvert] = useState(false);
  const [inputValue, setInputValue] = useState<number>(0);
  const [conversionResult, setConversionResult] = useState<number>(0);
  const [isScoreToToken, setIsScoreToToken] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserScoreAndToken();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setConversionResult(isScoreToToken ? inputValue / 100 : inputValue * 10);
  }, [inputValue, isScoreToToken]);

  const fetchUserScoreAndToken = () => {
    const tokenStr = Cookies.get('token');
    if (!tokenStr) return;

    fetch(`${process.env.REACT_APP_API_URL}/v1/user/score`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${tokenStr}`, 'Content-Type': 'application/json' },
    })
      .then(response => response.json())
      .then(data => setScore(data.score))
      .catch(error => console.error('Error fetching user score:', error));

    fetch(`${process.env.REACT_APP_API_URL}/v1/user/getusertoken`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${tokenStr}`, 'Content-Type': 'application/json' },
    })
      .then(response => response.json())
      .then(data => setToken(parseFloat(data.token)))
      .catch(error => console.error('Error fetching user token:', error));
  };

  const formatNumber = (num: number) => num.toLocaleString();

  const handleTransfer = () => {
    if (inputValue <= 0) {
      setErrorMessage('Please enter a positive number.');
      return;
    }
    if (isScoreToToken && inputValue > score) {
      setErrorMessage('Not enough score.');
      return;
    }
    if (!isScoreToToken && inputValue > token) {
        setErrorMessage('Not enough tokens.');
        return;
    }
    setErrorMessage('');
    setOpenTransfer(true);
  };

  const confirmTransfer = () => {
    setLoading(true);
    const url = isScoreToToken
      ? `${process.env.REACT_APP_API_URL}/v1/user/transfertotoken`
      : `${process.env.REACT_APP_API_URL}/v1/user/transfertoscore`;

    fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${Cookies.get('token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ [isScoreToToken ? 'score' : 'token']: inputValue }),
    })
      .then(response => {
        if (!response.ok) throw new Error('Transfer failed');
        return response.json();
      })
      .then(() => {
        setTimeout(() => {
          setLoading(false);
          setSnackbarMessage('Transfer successful!');
          setSnackbarOpen(true);
          setOpenTransfer(false);
          setOpenConvert(false);
          setInputValue(0);
          fetchUserScoreAndToken();
        }, 1500);
      })
      .catch(() => {
        setLoading(false);
        setSnackbarMessage('Transfer failed.');
        setSnackbarOpen(true);
      });
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <style>{keyframes}</style>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <Particles />
      </div>
      
      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      <Box sx={{ position: 'relative', zIndex: 1, pt: '100px', px: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
          <ActionButton darkMode={darkMode} variant="contained" onClick={() => navigate('/shop')}>
            Shop
          </ActionButton>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 4, color: theme.palette.text.primary }}>
            My Balance
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" sx={{ mb: 4 }}>
             <Box sx={{ p: 3, minWidth: '200px', borderRadius: '16px', background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', border: '1px solid rgba(128,128,128,0.2)' }}>
                <Typography variant="h6" color="textSecondary">(Clicker) Score</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>{formatNumber(score)}</Typography>
             </Box>
             <Box sx={{ p: 3, minWidth: '200px', borderRadius: '16px', background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', border: '1px solid rgba(128,128,128,0.2)' }}>
                <Typography variant="h6" color="textSecondary">Token</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>{formatNumber(token)}</Typography>
             </Box>
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="center">
            <ActionButton darkMode={darkMode} onClick={() => { setIsScoreToToken(true); setOpenConvert(true); }}>
              Convert (Clicker) Score to Token
            </ActionButton>
            <ActionButton darkMode={darkMode} onClick={() => { setIsScoreToToken(false); setOpenConvert(true); }}>
              Convert Token to (Clicker) Score
            </ActionButton>
          </Stack>
        </Box>
      </Box>

      {/* --- KONVERZIÓS DIALOG --- */}
      <AnimatedDialog 
        open={openConvert} 
        onClose={() => setOpenConvert(false)} 
        darkMode={darkMode}
        TransitionComponent={Fade}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Transfer {isScoreToToken ? 'Score to Token' : 'Token to Score'}</DialogTitle>
        <DialogContent>
          <StyledTextField
            darkMode={darkMode}
            fullWidth
            type="number"
            label={isScoreToToken ? 'Enter Score Amount' : 'Enter Token Amount'}
            value={inputValue || ''}
            onChange={(e) => setInputValue(Math.max(0, Number(e.target.value)))}
            variant="outlined"
            sx={{ mt: 1 }}
          />
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 3 }}>
            <Button size="small" onClick={() => setInputValue(0)} sx={{ color: darkMode ? 'white' : '#1a237e', borderColor: 'currentColor' }} variant="outlined">Clear</Button>
            {[100, 1000, 5000].map((val) => (
              <Button key={val} size="small" onClick={() => setInputValue(prev => prev + val)} sx={{ color: darkMode ? 'white' : '#1a237e', borderColor: 'currentColor' }} variant="outlined">
                +{formatNumber(val)}
              </Button>
            ))}
            <Button size="small" onClick={() => setInputValue(isScoreToToken ? score : token)} sx={{ color: darkMode ? '#FFD700' : '#d4af37', borderColor: 'currentColor', fontWeight: 'bold' }} variant="outlined">All</Button>
          </Box>

          {errorMessage && <Typography color="error" sx={{ mt: 2, textAlign: 'center', fontWeight: 'bold' }}>{errorMessage}</Typography>}

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, p: 2, borderRadius: '12px', background: darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.4)', border: '1px solid rgba(128,128,128,0.1)' }}>
            <SwapHorizIcon sx={{ fontSize: 40, mb: 1, color: darkMode ? 'white' : '#1a237e' }} />
            <Typography variant="body1" sx={{ color: darkMode ? 'white' : '#1a237e' }}>You will receive:</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: darkMode ? '#FFD700' : '#b8860b' }}>
              {formatNumber(conversionResult)} {isScoreToToken ? 'Tokens' : 'Scores'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={() => setOpenConvert(false)} sx={{ color: darkMode ? 'white' : '#1a237e' }}>Cancel</Button>
          <ActionButton darkMode={darkMode} variant="contained" onClick={handleTransfer}>
            Start the Transfer
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>

      {/* --- CONFIRM DIALOG --- */}
      <AnimatedDialog open={openTransfer} onClose={() => setOpenTransfer(false)} darkMode={darkMode}>
        <DialogTitle>Confirm Transfer</DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center', fontSize: '1.1rem', color: darkMode ? 'white' : '#1a237e' }}>
            Are you sure you want to transfer <br/>
            <strong>{formatNumber(inputValue)} {isScoreToToken ? 'Score' : 'Token'}</strong> <br/>
            for <br/>
            <strong>{formatNumber(conversionResult)} {isScoreToToken ? 'Tokens' : 'Scores'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 3, justifyContent: 'center', gap: 2 }}>
          <Button onClick={() => setOpenTransfer(false)} sx={{ color: darkMode ? 'white' : '#1a237e' }}>No, Go Back</Button>
          <ActionButton darkMode={darkMode} onClick={confirmTransfer}>
            Yes, Confirm
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>

      <Backdrop open={loading} sx={{ color: '#fff', zIndex: 2000 }}>
        <Stack alignItems="center" spacing={2}>
            <CircularProgress color="inherit" />
            <Typography variant="h6">Processing Transaction...</Typography>
        </Stack>
      </Backdrop>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={() => setSnackbarOpen(false)} 
        message={snackbarMessage} 
      />
    </div>
  );
};

export default MyScore;